import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react"
import { useTranslation } from "react-i18next"
import { CircularProgress } from "@mui/material"

import {
  ChartData,
  SessionDetailsData,
  ChartMultiple,
  ChartMultipleItem,
} from "@/app/report/common/constructor"
import { ChartStyle, createChart } from "@/app/report/common/chart/multiple"
import {
  REPORT_SECTION_PADDING,
  REPORT_PADDING,
  DRAWER_WIDTH,
  VALUE_POSITION_TYPE,
} from "@/app/report/common/constants"
import { createDrawerStore } from "@/stores/global"
import { useTranslateMetricKey } from "@/hooks/metric/useTranslateKeyHook"

import { ExportFileFunctionType } from "@/app/report/common/type"
import { CHART_RATIO, MIN_CHART_HEIGHT } from "@/app/report/common/export/constants"

import "./multipleChart.scss"

const DEFAULT_CHART_WIDTH = 880
const DEFAULT_CHART_HEIGHT = 250

function MultipleChart(
  {
    loading,
    chartData,
    scaleRatio,
    splitted,
    maxItemCount,
    decimalPoint = 1,
    chartAnnotation,
    highestValues,
  }: {
    loading: boolean
    splitted: boolean
    /** 한 섹션이 여러 조각으로 나뉠 때, 모든 조각이 같은 항목 폭을 갖도록 하는 기준 개수 */
    maxItemCount?: number
    chartData?: ChartData | SessionDetailsData
    scaleRatio?: number
    decimalPoint?: number
    chartAnnotation?: VALUE_POSITION_TYPE
    highestValues?: number[]
  },
  ref: React.ForwardedRef<{
    drawChart: () => void
    exportFile: (args: ExportFileFunctionType) => Promise<void>
  }>,
) {
  const { t } = useTranslation()

  // chart 관련
  const elementLegendAndChart = useRef<HTMLDivElement>(null)
  const elementLegends = useRef<HTMLDivElement[]>(null)
  const elementChartContainer = useRef<HTMLDivElement>(null)
  const chartYAxisLeft = useRef<HTMLDivElement>(null)
  const chartElements = useRef<HTMLDivElement>(null)
  const chartYAxisRight = useRef<HTMLDivElement>(null)

  // const elementHighlighted = useRef<HTMLDivElement>(null) TODO unused 제거
  const svgYAxisLeft = useRef<SVGSVGElement>(null)
  const svgYAxisRight = useRef<SVGSVGElement>(null)
  // 리사이징 관련
  const { isOpen: isDrawerOpen, isMobile } = createDrawerStore()
  const [chartWidth, setChartWidth] = useState<number>(0)
  const [chartHeight, setChartHeight] = useState<number>(0)
  // handleResize 함수가 컴포넌트 렌더링 전에 실행되는 것을 방지하기 위해 useRef 사용
  const isMounted = useRef(false)

  const translateKey = useTranslateMetricKey()

  useEffect(() => {
    elementLegends.current = []
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      handleResize()
    })

    observer.observe(document.querySelector(".DialogCont") as Element)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!loading && chartData && chartWidth && chartHeight) {
      drawChart()
    }
  }, [chartData, chartWidth, chartHeight, loading, t])

  const handleResize = () => {
    const pdfContainer = document.querySelector(".ExportPdfPreviewCont")
    const containerWidth =
      pdfContainer?.clientWidth || document.querySelector(".DialogCont")?.clientWidth
    if (containerWidth) {
      // 모바일에서는 drawer가 overlay이므로 width 조정하지 않음
      // store 타이밍 이슈로 window.innerWidth로 직접 체크
      const isMobileScreen = window.innerWidth < 1200
      const drawerOffset = isDrawerOpen && !isMobileScreen ? DRAWER_WIDTH : 0
      const currentChartWidth = pdfContainer
        ? containerWidth - 40
        : containerWidth - drawerOffset - REPORT_PADDING - REPORT_SECTION_PADDING - 1

      if (isMounted.current) {
        setChartWidth(currentChartWidth)
        // 세로 크롬이 고정 px 이라 폭이 좁아지면 플롯 영역이 먼저 사라진다 — 하한을 둔다.
        setChartHeight(Math.max(currentChartWidth * CHART_RATIO, MIN_CHART_HEIGHT))
      }
    }
  }

  // 초기 마운트 시 resize 실행
  useEffect(() => {
    handleResize()
  }, [])

  const drawChart = useCallback(async () => {
    if (
      !chartData ||
      !elementChartContainer ||
      !chartYAxisLeft ||
      !chartElements ||
      !chartYAxisRight
    ) {
      return
    }

    const chartStyle = new ChartStyle({
      // chartWidth 는 이미 실측 컨테이너 폭 — scaleRatio 를 다시 곱하면 이중 스케일로
      // 컨테이너보다 넓게 그려져 우측이 잘린다(WEB-2259). fallback 상수에만 배율 적용.
      width: chartWidth || DEFAULT_CHART_WIDTH * (scaleRatio || 1),
      height: chartHeight || DEFAULT_CHART_HEIGHT * (scaleRatio || 1),
      flagShowChartLabelOnXAxis: false,
      flagLongNameOnXAxis: true,
      magnification: 1,
    })

    chartStyle.flagShowTickStroke = false
    chartStyle.arrayWidthOfBar = [17, 13, 13]
    chartStyle.fullWidth = false
    chartStyle.alignLeft = false
    // 자투리 조각(예: 8개씩 자르고 6개 남은 마지막 조각)도 앞 조각과 같은 막대 폭을 쓰게 한다.
    chartStyle.referenceXAxisItemCount = maxItemCount ?? 0
    // 축 영역을 좁혀 차트 폭을 되찾는다. 출력용은 폰트가 작아(8px) 화면 기준 여백이 과하다.
    // 실측(축 3개): 우측 축 176px 중 눈금 글자 4개(각 5px)와 세로 라벨 2개(두께 ~12px)를 빼면
    // 100px 이상이 빈 공간이었다.
    chartStyle.space.yAxisLabelWidth = 16
    chartStyle.space.betweenRightYAxis = 6
    chartStyle.minYAxisTickValueWidth = 14
    chartStyle.valuePositionType = chartAnnotation as VALUE_POSITION_TYPE

    chartStyle.isPreview = true
    chartStyle.fontSize = 8
    chartStyle.fontSizeValue = 8
    chartStyle.fontSizeNoData = 8

    const targetData =
      chartData instanceof SessionDetailsData ? chartData.dataSession : chartData.data
    const charts = [new ChartMultipleItem("chart", null, targetData, highestValues)]
    const chartMultiple = new ChartMultiple(chartData.axisY, charts)

    const result = createChart({
      chartMultiple,
      style: chartStyle,
      mouseover: () => {},
      mousemove: () => {},
      mouseout: () => {},
      translateKey,
      decimalPointForExportFile: decimalPoint,
    })
    if (
      result &&
      elementChartContainer.current &&
      chartYAxisLeft.current &&
      chartElements.current &&
      chartYAxisRight.current
    ) {
      chartElements.current.innerHTML = ""
      chartYAxisLeft.current.innerHTML = ""
      chartYAxisRight.current.innerHTML = ""

      if (result.svgYAxisLeft) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisLeft.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisLeft.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisLeft)
        chartYAxisLeft.current.appendChild(nodeDiv)
        svgYAxisLeft.current = result.svgYAxisLeft
      }
      if (result.svgYAxisRight) {
        const nodeDiv = document.createElement("div")
        nodeDiv.style.width = `${result.svgYAxisRight.viewBox.baseVal.width}px`
        nodeDiv.style.height = `${result.svgYAxisRight.viewBox.baseVal.height}px`
        nodeDiv.appendChild(result.svgYAxisRight)
        chartYAxisRight.current.appendChild(nodeDiv)
        svgYAxisRight.current = result.svgYAxisRight
      }

      const svgCharts: SVGSVGElement[] = []

      result.svgCharts.forEach((svgChart) => {
        if (svgChart) {
          const nodeDiv = document.createElement("div")
          nodeDiv.style.width = `${svgChart.viewBox.baseVal.width}px`
          nodeDiv.style.height = `${svgChart.viewBox.baseVal.height}px`
          nodeDiv.appendChild(svgChart)
          chartElements.current?.appendChild(nodeDiv)
          svgCharts.push(svgChart)
        }
      })
    }
  }, [chartData, chartWidth, chartHeight])

  useImperativeHandle(ref, () => ({
    drawChart,
    exportFile: async () => {},
  }))

  if (loading) {
    return (
      <div className="MuiLoadingSpinnerCont">
        <CircularProgress size={48} />
      </div>
    )
  }

  return (
    <div className="chart preview">
      <div ref={elementLegendAndChart} className="element-legend-and-chart">
        <div className="legend">
          {chartData?.axisY?.map((y, i) => {
            return (
              <div
                ref={(el) => {
                  if (el && elementLegends.current) {
                    elementLegends.current[i] = el
                  }
                }}
                className="element-legend"
                key={y.key}
                style={{
                  color: y.color,
                }}>
                <span>●</span>
                <span className="legend-text">{y.name}</span>
              </div>
            )
          })}
        </div>
      </div>
      <div ref={elementChartContainer} className="element-chart-container">
        <table>
          <tbody>
            <tr>
              <td className="chart-y-axis-left">
                <div ref={chartYAxisLeft}></div>
              </td>
              <td>
                <div ref={chartElements} className="chart-elements"></div>
              </td>
              <td className="chart-y-axis-right">
                <div ref={chartYAxisRight}></div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default forwardRef(MultipleChart)
